import type { Product } from "../../products/hooks/useProducts";
import type { CardPricingRow } from "../../pos/hooks/usePosSession";

export type PosProductSection = "all" | "top" | "recent" | "quick";

export type ListStockPill = { variant: "out" | "low" | "neutral"; text: string };

export type ProductCardViewModel = {
  id: string;
  name: string;
  skuLabel: string;
  /** Second line in list view (matches design: category line) */
  listSubtitle: string;
  /** List row stock pill (short labels like reference) */
  listStockPill: ListStockPill;
  outOfStock: boolean;
  hasPricingRow: boolean;
  unitPriceLabel: string | null;
  mrpLabel: string | null;
  stockBadge: "out" | "low" | "ok";
  /** Shown on card (e.g. "12 left") */
  stockLabel: string;
  lowStockIndicator: string | null;
};

export function mapProductToCardView(
  product: Product,
  pricing: CardPricingRow | undefined,
  formatQty: (n: number) => string,
  formatInr: (n: number) => string,
): ProductCardViewModel {
  const pid = String(product.id);
  const hasBatch = Boolean(product.pos_active_batch);
  const pr = pricing;
  const outOfStock = !hasBatch || (pr ? pr.available_qty <= 0 : false);
  const qty = pr?.available_qty;
  let stockBadge: ProductCardViewModel["stockBadge"] = "ok";
  let stockLabel = "";
  let lowStockIndicator: string | null = null;
  let listStockPill: ListStockPill = { variant: "neutral", text: "—" };

  const listSubtitle =
    product.categories && product.categories.length > 0
      ? product.categories.map((c) => c.name).join(", ")
      : product.sku?.trim() || "";

  if (outOfStock) {
    stockBadge = "out";
    stockLabel = "Out of stock";
    listStockPill = { variant: "out", text: "Out" };
  } else if (!pr) {
    stockLabel = "Stock…";
    listStockPill = { variant: "neutral", text: "…" };
  } else if (pr && Number.isFinite(qty)) {
    if (qty! <= 0) {
      stockBadge = "out";
      stockLabel = "Out of stock";
      listStockPill = { variant: "out", text: "Out" };
    } else if (qty! < 10) {
      stockBadge = "low";
      stockLabel = `Low: ${formatQty(qty!)}`;
      listStockPill = { variant: "low", text: `Low: ${formatQty(qty!)}` };
      lowStockIndicator = `${formatQty(qty!)} left`;
    } else {
      stockLabel = `${formatQty(qty!)} left`;
      listStockPill = { variant: "neutral", text: formatQty(qty!) };
    }
  }

  return {
    id: pid,
    name: product.name,
    skuLabel: product.sku ? `SKU: ${product.sku}` : " ",
    listSubtitle: listSubtitle.trim() ? listSubtitle : "\u00A0",
    listStockPill,
    outOfStock,
    hasPricingRow: Boolean(pr),
    unitPriceLabel: pr ? formatInr(pr.unit_price) : null,
    mrpLabel: pr?.mrp != null ? formatInr(pr.mrp) : null,
    stockBadge,
    stockLabel,
    lowStockIndicator,
  };
}

/** Client-only subset for design "chips" (no backend). */
export function applyProductSectionFilter(
  products: Product[],
  section: PosProductSection,
  pricingById: Record<string, CardPricingRow>,
): Product[] {
  if (section === "all") return products;

  const score = (p: Product) => {
    const pr = pricingById[String(p.id)];
    if (!pr || !Number.isFinite(pr.available_qty)) return 0;
    return pr.available_qty;
  };

  if (section === "top" || section === "quick") {
    return [...products]
      .sort((a, b) => score(b) - score(a))
      .slice(0, 24);
  }

  if (section === "recent") {
    return [...products].reverse().slice(0, 24);
  }

  return products;
}

export function filterProductsByCategoryId(products: Product[], categoryId: string | null): Product[] {
  if (!categoryId) return products;
  return products.filter((p) => {
    if (p.category_ids?.includes(categoryId)) return true;
    return Boolean(p.categories?.some((c) => c.id === categoryId));
  });
}
